/**
 * Multi-chart workspace state.
 *
 * A workspace is an ordered list of tiles. Each tile remembers its own
 * symbol, timeframe and position in the grid, so adding a chart to look at
 * TSLA while the first one is on AAPL does not require touching the first
 * one. The layout survives reload, and the same workspace is visible from
 * every window pointed at `/workspace` -- a pop-out window reads the same
 * localStorage and listens for changes on a BroadcastChannel.
 *
 * The grid is twelve columns wide and open-ended downward. Positions are in
 * grid cells rather than pixels: a tile that is three cells wide stays three
 * cells wide when the window changes size, which is what makes "arrange
 * four charts in a 2x2" produce a 2x2 rather than four random boxes.
 */
import { useCallback, useEffect, useMemo, useState } from "react";

export const GRID_COLS = 12;
export const CELL_MIN = 2;      // smallest a tile can shrink to, in cells

export interface Tile {
  id: string;
  symbol: string;
  timeframe: string;
  /** Grid position in cells. Top-left is (0, 0). */
  x: number;
  y: number;
  w: number;
  h: number;
  /** Volume strip visibility: a per-tile preference, not a global one. */
  volume: "off" | "overlay" | "pane";
  /** Script id to run against this tile's bars, or null for none. The script
   *  itself lives in the users saved scripts; storing the id rather than the
   *  source keeps the workspace small and avoids a tile holding a stale copy
   *  if the script is later edited. */
  scriptId?: number | null;
  /** Indicator instance ids to overlay on this tile. The definitions and
   *  parameters are the same per-user set the single-chart view uses, so a
   *  "EMA 21" on this tile is the same configuration everywhere. */
  indicatorIds?: string[];
}

export interface Workspace {
  tiles: Tile[];
  /** When on, hovering a bar on one tile highlights the same timestamp on
   *  every other tile. Shared across tiles and windows -- it is a workspace
   *  preference, not per-tile. */
  crosshairSync: boolean;
}

const STORAGE_KEY = "tt.workspace.v1";
const CHANNEL_NAME = "tt.workspace";

/** Seed a workspace that is useful without any further input.
 *
 *  Four tiles in a 2x2: the index, two large caps, a volatility reference.
 *  Everyone has an opinion about this choice; everyone finds "replace it
 *  with my own" immediately obvious.
 */
function defaultWorkspace(): Workspace {
  return {
    tiles: [
      { id: tid(), symbol: "SPY",  timeframe: "5m",  x: 0, y: 0, w: 6, h: 4, volume: "overlay", scriptId: null, indicatorIds: [] },
      { id: tid(), symbol: "AAPL", timeframe: "5m",  x: 6, y: 0, w: 6, h: 4, volume: "overlay", scriptId: null, indicatorIds: [] },
      { id: tid(), symbol: "NVDA", timeframe: "5m",  x: 0, y: 4, w: 6, h: 4, volume: "overlay", scriptId: null, indicatorIds: [] },
      { id: tid(), symbol: "QQQ",  timeframe: "5m",  x: 6, y: 4, w: 6, h: 4, volume: "overlay", scriptId: null, indicatorIds: [] },
    ],
    crosshairSync: true,
  };
}

function tid(): string {
  // crypto.randomUUID() is available everywhere this will run; the fallback
  // is enough for the one browser that is not everywhere.
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "t-" + Math.random().toString(36).slice(2, 10);
}

function load(): Workspace {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultWorkspace();
    const parsed = JSON.parse(raw) as Workspace;
    if (!parsed || !Array.isArray(parsed.tiles) || parsed.tiles.length === 0) {
      return defaultWorkspace();
    }
    // Trust nothing read from storage: a tile from an older schema or a
    // hand-edited value should not crash the layout.
    return {
      tiles: parsed.tiles.map((t) => ({
        id: typeof t.id === "string" ? t.id : tid(),
        symbol: (t.symbol || "SPY").toUpperCase(),
        timeframe: t.timeframe || "5m",
        x: clampInt(t.x, 0, GRID_COLS - CELL_MIN),
        y: Math.max(0, Math.round(t.y) || 0),
        w: clampInt(t.w, CELL_MIN, GRID_COLS),
        h: Math.max(CELL_MIN, Math.round(t.h) || CELL_MIN),
        volume: (t.volume === "off" || t.volume === "pane") ? t.volume : "overlay",
        scriptId: typeof t.scriptId === "number" ? t.scriptId : null,
        indicatorIds: Array.isArray(t.indicatorIds)
          ? t.indicatorIds.filter((i) => typeof i === "string")
          : [],
      })),
      crosshairSync: typeof parsed.crosshairSync === "boolean" ? parsed.crosshairSync : true,
    };
  } catch {
    return defaultWorkspace();
  }
}

function clampInt(v: unknown, lo: number, hi: number): number {
  const n = Math.round(Number(v) || 0);
  return Math.max(lo, Math.min(hi, n));
}

function save(ws: Workspace): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ws));
  } catch {
    // Private browsing or quota exceeded. Nothing to recover; the layout
    // just does not persist for this session.
  }
}

/** React hook: the workspace, mutations, and cross-window sync.
 *
 *  Every mutation goes through here, posts to the channel, and persists.
 *  A pop-out window using this same hook receives the message, re-reads
 *  storage, and renders the new layout -- so dragging a tile in one window
 *  moves it in every open view of the workspace.
 */
export function useWorkspace() {
  const [ws, setWs] = useState<Workspace>(() => load());

  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;
    const chan = new BroadcastChannel(CHANNEL_NAME);
    chan.onmessage = (ev) => {
      // The event body is a bump counter rather than the state: whoever
      // reads next gets the authoritative copy from storage, so a slow
      // message cannot overwrite a quick local edit.
      if (ev.data?.kind === "changed") setWs(load());
    };
    return () => chan.close();
  }, []);

  const commit = useCallback((next: Workspace) => {
    setWs(next);
    save(next);
    if (typeof BroadcastChannel !== "undefined") {
      const chan = new BroadcastChannel(CHANNEL_NAME);
      chan.postMessage({ kind: "changed", at: Date.now() });
      chan.close();
    }
  }, []);

  const addTile = useCallback((symbol = "AAPL", timeframe = "5m") => {
    setWs((prev) => {
      const spot = findSpot(prev.tiles, 6, 4);
      const tile: Tile = {
        id: tid(), symbol, timeframe,
        x: spot.x, y: spot.y, w: spot.w, h: spot.h, volume: "overlay",
        scriptId: null, indicatorIds: [],
      };
      const next = { ...prev, tiles: [...prev.tiles, tile] };
      save(next);
      return next;
    });
  }, []);

  const removeTile = useCallback((id: string) => {
    setWs((prev) => {
      const next = { ...prev, tiles: prev.tiles.filter((t) => t.id !== id) };
      save(next);
      return next;
    });
  }, []);

  const updateTile = useCallback((id: string, patch: Partial<Tile>) => {
    setWs((prev) => {
      const next = {
        ...prev,
        tiles: prev.tiles.map((t) => (t.id === id ? { ...t, ...patch } : t)),
      };
      save(next);
      return next;
    });
  }, []);

  const setCrosshairSync = useCallback((on: boolean) => {
    setWs((prev) => {
      const next = { ...prev, crosshairSync: on };
      save(next);
      if (typeof BroadcastChannel !== "undefined") {
        const chan = new BroadcastChannel(CHANNEL_NAME);
        chan.postMessage({ kind: "changed", at: Date.now() });
        chan.close();
      }
      return next;
    });
  }, []);

  const resetLayout = useCallback(() => commit(defaultWorkspace()), [commit]);

  // Available tiles for the picker. Order matches rendering, which matches
  // visual order when the user has not reshuffled them.
  const byId = useMemo(() => {
    const m = new Map<string, Tile>();
    for (const t of ws.tiles) m.set(t.id, t);
    return m;
  }, [ws]);

  return { ws, byId, addTile, removeTile, updateTile, resetLayout, commit, setCrosshairSync };
}

/** Open space on the grid for a new tile of the requested footprint.
 *
 *  Walks row by row, left to right. If nothing fits in the current rows,
 *  places it below the lowest tile -- the grid grows downward rather than
 *  refusing to add. Six-by-four is the default new-tile size, matching the
 *  default four-tile seed, so adding a fifth tile does not look out of place.
 */
function findSpot(
  tiles: Tile[],
  wantW: number,
  wantH: number,
): { x: number; y: number; w: number; h: number } {
  const w = Math.min(wantW, GRID_COLS);
  const h = Math.max(CELL_MIN, wantH);
  const rows = tiles.reduce((m, t) => Math.max(m, t.y + t.h), 0);
  for (let y = 0; y <= rows; y++) {
    for (let x = 0; x + w <= GRID_COLS; x++) {
      if (!overlaps(tiles, x, y, w, h)) return { x, y, w, h };
    }
  }
  return { x: 0, y: rows, w, h };
}

function overlaps(tiles: Tile[], x: number, y: number, w: number, h: number): boolean {
  for (const t of tiles) {
    if (x + w <= t.x || t.x + t.w <= x) continue;
    if (y + h <= t.y || t.y + t.h <= y) continue;
    return true;
  }
  return false;
}
